import { AutomatonSectionContextMenu } from '@components/map/contextMenu/AutomatonSectionContextMenu';
import PerimeterCameraContextMenu from '@components/map/contextMenu/PerimeterCameraContextMenu';
import { ReplayEventContextMenu } from '@components/map/contextMenu/ReplayEventContextMenu';
import SensorContextMenu from '@components/map/contextMenu/sensor/SensorContextMenu';
import ZoneContextMenu from '@components/map/contextMenu/ZoneContextMenu';
import { useMapContext } from '@components/map/MapContext';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectIsPerimeterDoubtCheckInScreenConfiguration } from '@redux/config/config.selectors';
import { selectZoneViewMode } from '@redux/maps/maps.selectors';
import { AUTOMATON_SECTION_LAYER_NAMES, ZONE_LAYER_NAMES } from '@utils/map/map.constants';
import { getAvailableLayers } from '@utils/map/map.utils';
import { isZoneFeature } from '@utils/map/zone.utils';
import { isEqual } from 'lodash';
import { GeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import { Dispatch, SetStateAction } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { Target } from '@/types/c2/c2.types';
import { PointOfInterest } from '@/types/config/pointOfInterest.types';
import { FeatureTypeEnum, LayerNameEnum, SensorUniqueKeys, SourceNameEnum } from '@/types/map.types';
import { ReplayEvent } from '@/types/replay/replay.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { AutomatonSectionUniqueKeys } from '@/types/sensor/sensors';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import { DefaultContextMenu } from './DefaultContextMenu';
import PoiContextMenu from './PoiContextMenu';
import { TargetContextMenu } from './TargetContextMenu';
import { FeatureTypeToContextComponentEnum, featureTypeToContextComponentMap } from './type';

type ContextMenuProps = {
  event: MapMouseEvent;
  clickPosition: { x: number; y: number };
  selectedZoneFeature: GeoJSONFeature | null;
  onClose: () => void;
  setSelectedZoneFeature: Dispatch<SetStateAction<GeoJSONFeature | null>>;
};

const layers: LayerNameEnum[] = [
  ...AUTOMATON_SECTION_LAYER_NAMES,
  ...ZONE_LAYER_NAMES,
  LayerNameEnum.EVENTS,
  LayerNameEnum.PERIMETER_SENSOR_ICONS,
  LayerNameEnum.PERIMETER_SENSOR_LABELS,
  LayerNameEnum.PERIMETER_CAMERA_LABELS,
  LayerNameEnum.PERIMETER_CAMERA_SECTOR_FILLS,
  LayerNameEnum.POI,
  LayerNameEnum.SENSOR_CLUSTER_LEAVES,
  LayerNameEnum.SENSOR_ICONS,
  LayerNameEnum.SENSOR_LABELS,
  LayerNameEnum.TARGETS,
];

export function ContextMenuFactory({
  event,
  clickPosition,
  onClose,
  selectedZoneFeature,
  setSelectedZoneFeature,
}: Readonly<ContextMenuProps>) {
  const { current: mapbox } = useMap();
  const { mapId, sidebarOptions } = useMapContext();

  const isDoubtCheckEnabled = useAppSelector(selectIsPerimeterDoubtCheckInScreenConfiguration);
  const viewMode = useAppSelector((state) => selectZoneViewMode(state, mapId));

  if (!mapbox) {
    return null;
  }

  const availableLayers = getAvailableLayers(mapbox.getMap(), layers);
  const features = mapbox.queryRenderedFeatures(event.point, {
    layers: availableLayers,
  });

  if (availableLayers.length === 0 || features.length === 0) {
    return <DefaultContextMenu clickPosition={clickPosition} position={event.lngLat} onClose={onClose} />;
  }

  for (const feature of features) {
    const properties = feature.properties;
    if (!properties) continue;

    const featureType = properties.featureType;
    const value = properties.value;
    const parsed = value ? JSON.parse(value) : undefined;

    if (sidebarOptions.showZones && viewMode && isZoneFeature(featureType)) {
      if (selectedZoneFeature && selectedZoneFeature.id !== feature.id) {
        mapbox.setFeatureState(
          { source: SourceNameEnum.ZONES, id: selectedZoneFeature.id! },
          {
            isSelected: false,
          },
        );
      }
      !isEqual(selectedZoneFeature, feature) && setSelectedZoneFeature(feature);
      mapbox.setFeatureState({ source: SourceNameEnum.ZONES, id: feature.id! }, { isSelected: true });

      return (
        <ZoneContextMenu
          selectedZone={parsed}
          onClose={() => {
            selectedZoneFeature &&
              mapbox.setFeatureState(
                { source: SourceNameEnum.ZONES, id: selectedZoneFeature.id! },
                { isSelected: false },
              );
            setSelectedZoneFeature(null);
            onClose();
          }}
        />
      );
    }

    const contextType: FeatureTypeToContextComponentEnum =
      featureTypeToContextComponentMap[featureType as FeatureTypeEnum] ?? FeatureTypeToContextComponentEnum.DEFAULT;

    switch (contextType) {
      case FeatureTypeToContextComponentEnum.TARGET:
        return (
          <TargetContextMenu
            selectedTarget={parsed as Target<TargetLocation>}
            clickPosition={clickPosition}
            onClose={onClose}
          />
        );
      case FeatureTypeToContextComponentEnum.SENSOR:
        return (
          <SensorContextMenu
            sensorUniqueKeys={parsed as SensorUniqueKeys}
            clickPosition={clickPosition}
            onClose={onClose}
          />
        );
      case FeatureTypeToContextComponentEnum.AUTOMATON_SECTION:
        return (
          <AutomatonSectionContextMenu
            automatonSectionKeys={parsed as AutomatonSectionUniqueKeys}
            clickPosition={clickPosition}
            position={event.lngLat}
            onClose={onClose}
          />
        );
      case FeatureTypeToContextComponentEnum.PERIMETER_CAMERA:
        return (
          isDoubtCheckEnabled && (
            <PerimeterCameraContextMenu
              perimeterCamera={parsed as PerimeterCamera}
              clickPosition={clickPosition}
              onClose={onClose}
            />
          )
        );
      case FeatureTypeToContextComponentEnum.POI:
        return <PoiContextMenu poi={parsed as PointOfInterest} clickPosition={clickPosition} onClose={onClose} />;
      case FeatureTypeToContextComponentEnum.REPLAY_EVENT:
        return (
          <ReplayEventContextMenu replayEvent={parsed as ReplayEvent} clickPosition={clickPosition} onClose={onClose} />
        );
      default:
        return <DefaultContextMenu clickPosition={clickPosition} position={event.lngLat} onClose={onClose} />;
    }
  }

  return null;
}
